import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * ChangePasswordDialog — a signed-in person changes their own password.
 *
 * One form for all three apps: Student opens it from Account, Manager and
 * Admin from the sidebar's account menu. The app supplies the copy (Student
 * and Manager translate it, Admin is English) and `onSubmit`, which calls
 * its own API client and reduces the server's answer to a result. This
 * component owns the fields, the mismatch check, which message sits where,
 * and the success state.
 *
 * `size` carries the shape, as on `DialogContent`: `default` is Admin's dense
 * dialog with dense fields, `lg` is the customer dialog card with customer
 * fields. A customer dialog's title takes the page's type, so the app can
 * pass its heading class through `titleClassName`.
 *
 * The form lives inside the popup, so closing the dialog discards what was
 * typed: reopening always starts empty.
 */
import { useId, useRef, useState } from 'react';
import { Button } from './button.js';
import { cn } from './cn.js';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './dialog.js';
import { FieldDescription, FieldError, FieldLabel } from './field.js';
import { Input } from './input.js';
import { Notice } from './notice.js';
import { Spinner } from './spinner.js';
export function ChangePasswordDialog({ open, onOpenChange, copy, onSubmit, size = 'default', titleClassName, }) {
    return (_jsx(Dialog, { open: open, onOpenChange: onOpenChange, children: _jsx(DialogContent, { size: size, "data-testid": "change-password-dialog", className: cn(size === 'default' && 'max-w-md'), children: _jsx(ChangePasswordBody, { copy: copy, onSubmit: onSubmit, size: size, titleClassName: titleClassName, onDone: () => onOpenChange(false) }) }) }));
}
function ChangePasswordBody({ copy, onSubmit, size, titleClassName, onDone, }) {
    const ids = useId();
    const [fieldError, setFieldError] = useState(null);
    const [formError, setFormError] = useState(null);
    const [pending, setPending] = useState(false);
    const [done, setDone] = useState(false);
    const inputs = { current: useRef(null), new: useRef(null), confirm: useRef(null) };
    const dense = size === 'default';
    const fieldSize = dense ? 'default' : 'lg';
    const buttonSize = dense ? 'default' : 'lg';
    const title = cn(!dense && 'text-section-title font-bold', titleClassName);
    const text = dense ? undefined : 'text-body text-text-secondary';
    const body = dense ? 'px-6 py-5' : 'mt-5';
    function fail(field, message) {
        setFieldError({ field, message });
        inputs[field].current?.focus();
    }
    async function submit(event) {
        event.preventDefault();
        if (pending)
            return;
        // Uncontrolled fields, read once here: nothing renders per keystroke.
        const form = new FormData(event.currentTarget);
        const value = (name) => String(form.get(name) ?? '');
        setFieldError(null);
        setFormError(null);
        if (value('new') !== value('confirm')) {
            fail('confirm', copy.mismatch);
            return;
        }
        setPending(true);
        let result;
        try {
            result = await onSubmit(value('current'), value('new'));
        }
        catch {
            result = 'failed';
        }
        setPending(false);
        if (result === 'ok')
            setDone(true);
        else if (result === 'current_incorrect')
            fail('current', copy.currentIncorrect);
        else if (result === 'too_weak')
            fail('new', copy.tooWeak);
        else
            setFormError(result === 'rate_limited' ? copy.rateLimited : copy.failed);
    }
    if (done) {
        return (_jsxs(_Fragment, { children: [_jsx(Header, { dense: dense, children: _jsx(DialogTitle, { className: title, children: copy.successTitle }) }), _jsxs("div", { className: cn(body, 'flex flex-col gap-5'), children: [_jsx(Notice, { variant: "success", role: "status", children: copy.successBody }), _jsx("div", { className: "flex justify-end", children: _jsx(Button, { variant: "primary", size: buttonSize, block: !dense, onClick: onDone, children: copy.done }) })] })] }));
    }
    const field = (name, label, autoComplete, hint) => {
        const id = `${ids}-${name}`;
        const error = fieldError?.field === name ? fieldError.message : null;
        const described = [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(' ');
        return (_jsxs("div", { children: [_jsx(FieldLabel, { htmlFor: id, label: label, size: fieldSize }), _jsx(Input, { ref: inputs[name], id: id, name: name, type: "password", size: fieldSize, autoComplete: autoComplete, required: true, "aria-invalid": error ? true : undefined, "aria-describedby": described || undefined }), hint && _jsx(FieldDescription, { id: `${id}-hint`, children: hint }), _jsx(FieldError, { id: `${id}-error`, message: error, size: fieldSize })] }));
    };
    const cancel = (_jsx(Button, { variant: "secondary", size: buttonSize, block: !dense, onClick: onDone, disabled: pending, children: copy.cancel }));
    return (_jsxs(_Fragment, { children: [_jsxs(Header, { dense: dense, children: [_jsx(DialogTitle, { className: title, children: copy.title }), _jsx(DialogDescription, { className: cn(text, !dense && 'mt-2'), children: copy.description })] }), _jsxs("form", { className: cn(body, 'flex flex-col gap-4'), onSubmit: (event) => void submit(event), children: [formError && _jsx(Notice, { variant: "danger", children: formError }), field('current', copy.currentLabel, 'current-password'), field('new', copy.newLabel, 'new-password', copy.newHint), field('confirm', copy.confirmLabel, 'new-password'), _jsxs("div", { className: cn('mt-2 flex gap-3', dense ? 'justify-end' : 'flex-col'), children: [dense && cancel, _jsxs(Button, { type: "submit", variant: "primary", size: buttonSize, block: !dense, pending: pending, children: [pending && _jsx(Spinner, { surface: "accent", tone: "current", size: 16, thickness: 2 }), pending ? copy.submitting : copy.submit] }), !dense && cancel] })] })] }));
}
function Header({ dense, children }) {
    return dense ? _jsx(DialogHeader, { children: children }) : _jsx(_Fragment, { children: children });
}
//# sourceMappingURL=change-password-dialog.js.map